import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  useReactFlow,
  useNodeId,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import {
  Button,
  Card,
  CardBody,
  Input,
  InputGroup,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import { toast } from "react-toastify";
import { convertProfileLocalDate } from "../../../../Utils/configController";
import AirHomeLoader from "../../../../components/Common/AirHomeLoader";

export type SegmentNodeData = {
  segmentId: string;
  segmentName?: string;
  /** Snapshot for showing same card on node when selected */
  description?: string;
  totalUsersCount?: number;
  percentage?: number;
  updateCycle?: string;
  lastUpdatedAt?: string;
  isActive?: boolean;
  isLiveSegmentation?: boolean;
  /** Passed from canvas (no context) */
  segments?: { _id: string; name: string }[];
  templates?: { value: string; label: string }[];
};

export type SegmentNodeType = Node<SegmentNodeData, "segment">;

const SEGMENT_DEBOUNCE_MS = 400;

function SegmentNode(props: NodeProps<SegmentNodeType>) {
  const { data } = props;
  const nodeId = useNodeId();
  const { setNodes, getNodes } = useReactFlow();
  const contextSegments = data.segments ?? [];
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [segmentList, setSegmentList] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchedDetailsRef = useRef(false);
  const detailsFetchInFlightRef = useRef(false);

  // When node has segmentId but no card details (e.g. edit load without row.segment populated), fetch segment details once. Only when offcanvas is closed to avoid double LIST_FILTER_SEGMENT call with offcanvas list fetch.
  const hasCardDetails =
    data.totalUsersCount !== undefined ||
    data.percentage !== undefined ||
    data.updateCycle !== undefined ||
    data.lastUpdatedAt !== undefined ||
    (data.description !== undefined && data.description !== "");

  useEffect(() => {
    if (
      offcanvasOpen ||
      !data.segmentId ||
      fetchedDetailsRef.current ||
      detailsFetchInFlightRef.current ||
      hasCardDetails
    )
      return;

    let cancelled = false;
    detailsFetchInFlightRef.current = true;
    const fetchSegmentDetails = async () => {
      try {
        const response = await Request.post(
          `${url.BASE_URL}${url.LIST_FILTER_SEGMENT}`,
          {
            draw: 1,
            start: 0,
            length: 100,
            search: { value: data.segmentId || data.segmentName || "" },
          },
        );
        if (cancelled) return;
        const list = response.data?.data || [];
        const seg = list.find((s: any) => s._id === data.segmentId);
        if (seg && nodeId) {
          fetchedDetailsRef.current = true;
          setNodes(
            getNodes().map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...(n.data || {}),
                      segmentName: seg.name,
                      description: seg.description,
                      totalUsersCount: seg.totalUsersCount,
                      percentage: seg.percentage,
                      updateCycle: seg.updateCycle,
                      lastUpdatedAt: seg.lastUpdatedAt,
                      isActive: seg.isActive,
                      isLiveSegmentation: seg.isLiveSegmentation,
                    },
                  }
                : n,
            ),
          );
        }
      } catch (err: any) {
        if (!cancelled) console.error("Error fetching segment details:", err);
      } finally {
        if (!cancelled) detailsFetchInFlightRef.current = false;
      }
    };
    fetchSegmentDetails();
    return () => {
      cancelled = true;
      detailsFetchInFlightRef.current = false;
    };
  }, [
    offcanvasOpen,
    data.segmentId,
    data.segmentName,
    hasCardDetails,
    nodeId,
    setNodes,
    getNodes,
  ]);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1);
    }, SEGMENT_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  // Fetch segments from same API as SegmentTable (server-side search + pagination). Only when offcanvas is open; ignore response if effect cleaned up to avoid duplicate/out-of-order updates.
  useEffect(() => {
    if (!offcanvasOpen) return;

    let cancelled = false;
    setLoading(true);

    const fetchData = async () => {
      try {
        const reqObj = {
          draw: 1,
          start: (page - 1) * perPage,
          length: perPage,
          search: { value: debouncedSearch || "" },
        };
        const response = await Request.post(
          `${url.BASE_URL}${url.LIST_FILTER_SEGMENT}`,
          reqObj,
        );
        if (cancelled) return;
        setSegmentList(response.data?.data || []);
        setTotalRows(response.data?.recordsTotal || 0);
      } catch (err: any) {
        if (cancelled) return;
        console.error("Error fetching segments:", err);
        toast.error("Failed to load segments");
        setSegmentList([]);
        setTotalRows(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [offcanvasOpen, page, perPage, debouncedSearch]);

  const selectSegment = (s: any) => {
    setNodes(
      getNodes().map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...(n.data || {}),
                segmentId: s._id,
                segmentName: s.name,
                description: s.description,
                totalUsersCount: s.totalUsersCount,
                percentage: s.percentage,
                updateCycle: s.updateCycle,
                lastUpdatedAt: s.lastUpdatedAt,
                isActive: s.isActive,
                isLiveSegmentation: s.isLiveSegmentation,
              },
            }
          : n,
      ),
    );
    setSearchText("");
    setOffcanvasOpen(false);
  };

  const closeOffcanvas = () => {
    setSearchText("");
    setPage(1);
    setOffcanvasOpen(false);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const startRow = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const endRow = Math.min(page * perPage, totalRows);

  const selectedName = data.segmentId
    ? data.segmentName ||
      segmentList.find((s: any) => s._id === data.segmentId)?.name ||
      contextSegments.find((s: any) => s._id === data.segmentId)?.name ||
      "Selected"
    : null;

  const hasSelection = !!data.segmentId;

  return (
    <>
      <div
        style={{
          minWidth: hasSelection ? 220 : 180,
          maxWidth: hasSelection ? 220 : undefined,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 8,
            padding: "2px",
            color: "#64748b",
            cursor: "grab",
            textAlign: "center",
            borderBottom: hasSelection ? "1px solid #e2e8f0" : undefined,
          }}
        >
          Segment
        </div>
        <div
          className="nodrag"
          onClick={() => setOffcanvasOpen(true)}
          style={{ cursor: "pointer" }}
        >
          {hasSelection ? (
            <Card
              className="border-0 shadow-none mb-0 rounded-0"
              style={{ borderRadius: 0 }}
            >
              <CardBody className="p-1 px-2 position-relative">
                {/* Status: Live badge + active/inactive dot — top right */}
                {data.isLiveSegmentation != null || data.isActive != null ? (
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 10,
                    }}
                  >
                    {data.isLiveSegmentation ? (
                      <span className="badge bg-info" style={{ fontSize: 7 }}>
                        Live
                      </span>
                    ) : null}
                    <span
                      title={data.isActive ? "Active" : "Inactive"}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: data.isActive ? "#198754" : "#dc3545",
                        flexShrink: 0,
                      }}
                    />
                  </div>
                ) : null}
                {/* Segment name — single line, ellipsis */}
                <div
                  className=""
                  style={{
                    paddingRight: 48,
                  }}
                >
                  <span
                    title={selectedName ?? data.segmentName ?? ""}
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#334155",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedName ?? data.segmentName ?? "Selected"}
                  </span>
                </div>
                {/* Description — separate, max 2 lines */}
                {data.description ? (
                  <p
                    className="small text-muted mb-1"
                    style={{
                      fontSize: 9,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      lineHeight: 1.35,
                      marginBottom: 0,
                    }}
                  >
                    {data.description}
                  </p>
                ) : null}
                {/* Metrics grid */}
                {data.totalUsersCount != null ||
                data.percentage != null ||
                data.updateCycle ||
                data.lastUpdatedAt ? (
                  <div
                    className="pt-2 border-top"
                    style={{
                      fontSize: 8,
                      color: "#64748b",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "6px 8px",
                    }}
                  >
                    <span>
                      <span className="text-muted" style={{ fontSize: 8 }}>
                        Users
                      </span>
                      <span
                        className="d-block"
                        style={{
                          fontWeight: 600,
                          color: "#334155",
                          fontSize: 9,
                        }}
                      >
                        {typeof data.totalUsersCount === "number"
                          ? data.totalUsersCount.toLocaleString()
                          : data.totalUsersCount ?? 0}
                      </span>
                    </span>
                    {data.percentage != null ? (
                      <span>
                        <span className="text-muted" style={{ fontSize: 8 }}>
                          Percentage
                        </span>
                        <span
                          className="d-block"
                          style={{
                            fontWeight: 600,
                            color: "#334155",
                            fontSize: 9,
                          }}
                        >
                          {data.percentage}%
                        </span>
                      </span>
                    ) : null}
                    {data.updateCycle ? (
                      <span>
                        <span className="text-muted" style={{ fontSize: 8 }}>
                          Cycle
                        </span>
                        <span
                          className="d-block text-capitalize"
                          style={{
                            fontWeight: 600,
                            color: "#334155",
                            fontSize: 9,
                          }}
                        >
                          {data.updateCycle}
                        </span>
                      </span>
                    ) : null}
                    {data.lastUpdatedAt ? (
                      <span>
                        <span className="text-muted" style={{ fontSize: 8 }}>
                          Updated
                        </span>
                        <span
                          className="d-block"
                          style={{
                            fontWeight: 600,
                            color: "#334155",
                            fontSize: 9,
                          }}
                        >
                          {convertProfileLocalDate(data.lastUpdatedAt)}
                        </span>
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <div
                  className="text-center mt-1 pt-1 border-top"
                  style={{ borderColor: "#e2e8f0" }}
                >
                  <span
                    className="text-primary"
                    style={{
                      fontSize: 9,
                      fontWeight: 500,
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    Click to change
                  </span>
                </div>
              </CardBody>
            </Card>
          ) : (
            <div className="px-2 py-2">
              <Button
                color="light"
                size="sm"
                outline
                className="w-100 d-flex align-items-center justify-content-center"
                style={{
                  fontSize: 10,
                  minHeight: 26,
                  border: "1px dashed #adb5bd",
                  borderRadius: 6,
                  fontWeight: 500,
                  color: "#495057",
                  backgroundColor: "#f8f9fa",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOffcanvasOpen(true);
                }}
              >
                Select segment
              </Button>
            </div>
          )}
        </div>
        <Handle
          type="source"
          position={Position.Right}
          id="segment-out"
          style={{ top: "50%" }}
        />
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        style={{ width: "90%" }}
        className="nodrag nopan"
      >
        <OffcanvasHeader
          toggle={closeOffcanvas}
          className="d-flex align-items-center justify-content-between  "
        >
          <h5
            className="modal-title "
            id="staticBackdropLabel"
            style={{
              textAlign: "center",
              fontWeight: 800,
            }}
          >
            Select segment
          </h5>
        </OffcanvasHeader>
        <OffcanvasBody
          className="d-flex flex-column p-0"
          style={{ overflow: "hidden", backgroundColor: "#f7f7fa" }}
        >
          <div className="px-3 py-2 flex-shrink-0">
            <InputGroup size="sm">
              <Input
                type="search"
                placeholder="Search by name or description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="border"
                style={{ fontSize: 13 }}
              />
            </InputGroup>
          </div>
          {loading && <AirHomeLoader />}
          <div className="d-flex flex-row flex-wrap gap-2 px-3 pb-2 flex-grow-1 overflow-auto align-content-start">
            {!loading && segmentList.length === 0 ? (
              <p className="text-muted small mb-0">
                {totalRows === 0 && !debouncedSearch
                  ? "No segments available."
                  : "No segments match your search."}
              </p>
            ) : (
              !loading &&
              segmentList.map((s: any) => (
                <Card
                  key={s._id}
                  tag="button"
                  type="button"
                  className="text-start border shadow-sm flex-shrink-0"
                  style={{
                    cursor: "pointer",
                    transition: "box-shadow 0.15s ease",
                    minWidth: 330,
                    maxWidth: 330,
                    padding: 0,
                    margin: 0,
                  }}
                  onClick={() => selectSegment(s)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody className="p-2 position-relative">
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 8,
                      }}
                    >
                      {s.isLiveSegmentation ? (
                        <span
                          className="badge bg-info"
                          style={{ fontSize: 10 }}
                        >
                          Live
                        </span>
                      ) : null}
                      <span
                        title={s.isActive ? "Active" : "Inactive"}
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: s.isActive ? "#198754" : "#dc3545",
                          flexShrink: 0,
                        }}
                      />
                    </div>
                    <div className="mb-1">
                      <span style={{ fontSize: 16, fontWeight: 600 }}>
                        {s.name}
                      </span>
                    </div>
                    {s.description ? (
                      <p
                        className="small text-muted mb-1"
                        style={{ fontSize: 12 }}
                      >
                        {s.description}
                      </p>
                    ) : null}
                    <div
                      className="pt-1 border-top"
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 4px",
                      }}
                    >
                      <span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          Users
                        </span>
                        <span
                          className="d-block"
                          style={{
                            fontWeight: 600,
                            color: "#334155",
                            fontSize: 13,
                          }}
                        >
                          {typeof s.totalUsersCount === "number"
                            ? s.totalUsersCount.toLocaleString()
                            : s.totalUsersCount ?? 0}
                        </span>
                      </span>
                      {s.percentage != null ? (
                        <span>
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            Percentage
                          </span>
                          <span
                            className="d-block"
                            style={{
                              fontWeight: 600,
                              color: "#334155",
                              fontSize: 13,
                            }}
                          >
                            {s.percentage}%
                          </span>
                        </span>
                      ) : null}
                      {s.updateCycle ? (
                        <span>
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            Cycle
                          </span>
                          <span
                            className="d-block text-capitalize"
                            style={{
                              fontWeight: 600,
                              color: "#334155",
                              fontSize: 13,
                            }}
                          >
                            {s.updateCycle}
                          </span>
                        </span>
                      ) : null}
                      {s.lastUpdatedAt ? (
                        <span>
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            Updated
                          </span>
                          <span
                            className="d-block"
                            style={{
                              fontWeight: 600,
                              color: "#334155",
                              fontSize: 13,
                            }}
                          >
                            {convertProfileLocalDate(s.lastUpdatedAt)}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
          {!loading && totalRows > 0 && (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-shrink-0"
              style={{ fontSize: 12, backgroundColor: "#ffffff" }}
            >
              <span>
                Showing {startRow}-{endRow} of {totalRows}
              </span>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", fontSize: 12 }}
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
                <Button
                  color="light"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  color="light"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
}

export default SegmentNode;
